import { useEffect, useReducer } from 'react';
import {
    TyrePageErrors,
    TyrePageState,
    TyreProcessStages,
    TyreRepairRequest,
    ReducerActionNames,
    nonUSTyreDamageTypes,
    TyreSpecificErrors
} from '../sitePages/tyreRepairRequest/TyreRepairRequestTypes';
import TyreRepairRequestReducer from '../sitePages/tyreRepairRequest/TyreRepairRequestReducer';
import {
    ProductTypes,
    RoutePaths,
    SelectOptionsYesNo,
    TyreRepairOptions,
    UploadImageTypes
} from '../../Constants/Common';
import { useNavigate } from 'react-router-dom';
import { RepairRequestApi } from '../../api/RepairRequestApi';
import { SaveTyreRepairRequest } from '../../api/RepairRequestTypes';
import { useAuth0 } from '@auth0/auth0-react';
import { ClientDateHandler } from '../common/clientDateHandler/ClientDateHandler';
import { CommonValidation } from '../common/commonValidation/commonValidation';
import { PolicyHolderApi } from '../../api/PolicyHolderApi';
import {
    AddressType,
    DealerAddressType,
    PolicyHolderType,
    ProductType,
    UploadedDocument,
    UploadedImage,
    VehicleType
} from '../common/CommonTypes';
import { ProgressBarItem } from '../common/progressBar/ProgressBar';
import { TyreRepairRequestContextState } from '../../context/TyreRepairRequestContext';
import { GoogleAnalytics } from '../../classes/GoogleAnalytics';
import { ConfirmationLocationState } from '../sitePages/ConfirmationPage';
import { useTranslation } from 'react-i18next';
import { SelectInputHelper } from '../common/selectInput/SelectInput';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { selectTyrePreCheck } from 'state/selectors/tyreRequest';
import { resetTyreRequest } from 'state/slices/tyreRequestSlice';
import {
    selectIsUkSite,
    selectIsUSSite,
    selectSiteCountry
} from 'state/selectors/site';

function useTyreRepairRequest(
    initialState: TyrePageState,
    product: ProductType,
    vehicle: VehicleType
): TyreRepairRequestContextState {
    const { user, getAccessTokenSilently } = useAuth0();
    const navigate = useNavigate();
    const dispatchAction = useAppDispatch();

    const [tyreRepairRequest, dispatch] = useReducer(
        TyreRepairRequestReducer,
        initialState
    );
    const preCheckRequest = useAppSelector(selectTyrePreCheck);

    const isUSSite = useAppSelector(selectIsUSSite);
    const isUkSite = useAppSelector(selectIsUkSite);
    const siteLocation = useAppSelector(selectSiteCountry);

    const showUserRepairAddress = useAppSelector(
        (s) => s.site.showRepairAddress
    ); // comes from BE as a sort of feature flag

    const sendGarageAddress =
        preCheckRequest.hasBeenRepaired === SelectOptionsYesNo.Yes &&
        preCheckRequest.hasBeenPayed === SelectOptionsYesNo.No &&
        tyreRepairRequest.repairOption === TyreRepairOptions.PreferredGarage;

    const sendRepairAddress = showUserRepairAddress || sendGarageAddress;

    const { t } = useTranslation();

    useEffect(() => {
        const getPolicyHolderDetails = async () => {
            const token = await getAccessTokenSilently();

            new PolicyHolderApi().getPolicyHolderDetails(
                product.reference,
                token,
                onGetPolicyHolderSuccess,
                onGetPolicyHolderFailure
            );
        };

        if (!isUSSite) {
            getPolicyHolderDetails();
        }
    }, []);

    const onGetPolicyHolderSuccess = (
        policyHolderDetails: PolicyHolderType
    ) => {
        if (showUserRepairAddress) {
            dispatch({
                type: ReducerActionNames.SetPolicyData,
                address: policyHolderDetails.address
            });
        }
    };

    const onGetPolicyHolderFailure = (error: string) => {
        console.log(`Failed to get the policy holder details: ${error}`);
    };

    const {
        tyreStage,
        mileage,
        repairOption,
        permissionGranted,
        tyreRepairRequests,
        currentRepairRequest,
        repairAddresses,
        selectedRepairAddressId,
        updateTyre,
        scrollToAddress,
        dateOfIncident
    } = tyreRepairRequest;

    const progressBarItems: ProgressBarItem[] = [
        {
            order: 1,
            text: t('tyre_repair_request.progress_bar.text.general'),
            active: tyreStage === TyreProcessStages.General,
            disabled: false,
            visible: true
        },
        {
            order: 2,
            text: t('tyre_repair_request.progress_bar.text.tyre_selection'),
            active: tyreStage === TyreProcessStages.TyreSelection,
            disabled: false,
            visible: true
        },
        {
            order: 3,
            text: t('tyre_repair_request.progress_bar.text.summary'),
            active: tyreStage === TyreProcessStages.Summary,
            disabled: false,
            visible: true
        },
        {
            order: 4,
            text: t('tyre_repair_request.progress_bar.text.confirmation'),
            active: false,
            disabled: false,
            visible: true
        }
    ];

    const onChangeMileage = (value: string): void => {
        dispatch({
            type: ReducerActionNames.UpdateMileage,
            mileage: value
        });
    };

    const onChangeRepairOption = (value: string): void => {
        dispatch({
            type: ReducerActionNames.UpdateRepairOption,
            repairOption: value
        });
    };

    const onChangDateOfIncident = (value: string): void => {
        dispatch({
            type: ReducerActionNames.UpdateIncidentDate,
            dateOfIncident: value
        });
    };

    const onTyreValueChange = (key: keyof TyreRepairRequest, value: string) => {
        dispatch({
            type: ReducerActionNames.UpdateTyreRepairRequest,
            key,
            value
        });
    };

    const onTyreRepairRequestChangeTyre = (
        value: string,
        key: keyof TyreRepairRequest
    ): void => {
        dispatch({
            type: ReducerActionNames.UpdateTyreRepairRequest,
            key,
            value
        });
    };

    const onTyreSelected = (tyre: string) => {
        dispatch({
            type: ReducerActionNames.SetTyre,
            tyre,
            productReference: product.reference
        });
    };

    const onChangeDamageType = (selectionName: string) => {
        dispatch({
            type: ReducerActionNames.UpdateTyreRepairRequest,
            key: 'damageType',
            value: selectionName
        });
    };

    const onInvoiceChange = (invoice: UploadedDocument) => {
        dispatch({
            type: ReducerActionNames.UpdateInvoice,
            invoice
        });
    };

    const onRepairAddressSelectionChanged = (selectedId: string | null) => {
        dispatch({
            type: ReducerActionNames.UpdateRepairAddressSelected,
            value: selectedId
        });
    };

    const onRepairAddressesSet = (repairAddresses: AddressType[]) => {
        dispatch({
            type: ReducerActionNames.SetRepairAddresses,
            addresses: repairAddresses
        });
    };

    const onRepairAddressAdded = (address: AddressType) => {
        dispatch({
            type: ReducerActionNames.AddRepairAddress,
            address
        });
    };

    const onRepairAddressUpdated = (id: string, address: AddressType) => {
        dispatch({
            type: ReducerActionNames.UpdateRepairAddress,
            id,
            address
        });
    };

    const onDealerAddressUpdate = (dealerAddress: DealerAddressType) => {
        dispatch({
            type: ReducerActionNames.UpdateDealerAddress,
            address: dealerAddress
        });
    };

    const onUploadImageChange = (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => {
        const uploadedImages = currentRepairRequest.uploadedImages;

        const uploadedImage = uploadedImages.find((ui) => ui.type == imageType);

        if (uploadedImage) {
            uploadedImage.uploadedPath = uploadedPath;
            uploadedImage.image = CommonValidation.isNotEmpty(uploadedPath)
                ? image
                : undefined;
            uploadedImage.filename = CommonValidation.isNotEmpty(uploadedPath)
                ? filename
                : undefined;
            uploadedImage.imageId = imageId;

            dispatch({
                type: ReducerActionNames.UpdateUploadedImages,
                uploadedImages: uploadedImages
            });
        }
    };

    const onTogglePermission = (isChecked: boolean) => {
        dispatch({
            type: ReducerActionNames.SetPermissionGranted,
            permissionGranted: isChecked
        });
    };

    const onChangeStage = (stage: TyreProcessStages): void => {
        dispatch({
            type: ReducerActionNames.UpdateTyreStage,
            newStage: stage
        });

        if (CommonValidation.isNotEmpty(updateTyre)) {
            onUpdateTyreClick('');
        }

        if (scrollToAddress) {
            onUpdateAddressClick(false);
        }
    };

    const onFinishedTyreRepairRequest = () => {
        const validPage = validateRepairPage();

        if (validPage) {
            saveRepairRequest();
        }
    };
    const onClearTyreRepairRequests = () => {
        dispatch({
            type: ReducerActionNames.ClearRepairRequests
        });
    };

    const validateRepairPage = () => {
        let errors: TyrePageErrors = {};
        let isValid = true;

        if (!permissionGranted) {
            isValid = false;
            errors = {
                ...errors,
                permissionGranted: t('validate_agreement')
            };
        }

        dispatch({
            type: ReducerActionNames.UpdateTyreErrors,
            tyreErrors: errors
        });

        console.log(isValid);

        return isValid;
    };

    const validateGeneralPage = () => {
        let errors: TyrePageErrors = {};
        let isValid = true;

        if (CommonValidation.isEmpty(mileage)) {
            isValid = false;
            errors = {
                ...errors,
                mileage: t('approximate_mileage_is_required')
            };
        }
        if (!CommonValidation.isValidNumeric(mileage)) {
            isValid = false;
            errors = {
                ...errors,
                mileage: t('approx_mileage_is_number')
            };
        }

        if (!isUSSite && !repairOption) {
            isValid = false;
            errors.repairOption = t('repair_option_is_required');
        }

        if (
            (dateOfIncident &&
                CommonValidation.isDateNull(dateOfIncident, true)) ||
            (dateOfIncident && CommonValidation.isEmpty(dateOfIncident))
        ) {
            isValid = false;
            errors = {
                ...errors,
                dateOfIncident: t('date_of_incident_is_required')
            };
        }

        if (sendRepairAddress && selectedRepairAddressId === null) {
            isValid = false;
            errors = {
                ...errors,
                noRepairAddressSelected: sendGarageAddress
                    ? t('garage_address_is_required')
                    : t('select_repair_address')
            };
        }

        dispatch({
            type: ReducerActionNames.UpdateTyreErrors,
            tyreErrors: errors
        });

        return isValid;
    };

    const saveRepairRequest = async () => {
        const successCallback = (repairRequests) => {
            const activeConfirmationItems = progressBarItems.map((p) =>
                p.text === 'Confirmation'
                    ? { ...p, active: true }
                    : { ...p, active: false }
            );

            const state: ConfirmationLocationState = {
                progressBarItems: activeConfirmationItems,
                repairRequests,
                productType: ProductTypes.Tyre
            };

            navigate(RoutePaths.Confirmation, { state });
        };

        const failureCallback = (error) => {
            console.log(`Failed to register policy: ${error}`);

            navigate(RoutePaths.ConfirmationError, {
                state: { repairRequestType: ProductTypes.Tyre }
            });
        };

        dispatch({
            type: ReducerActionNames.LoadingOnRequestSubmission,
            loadingOnRequestSubmission: true
        });

        const repairRequests = tyreRepairRequests.map(getRepairRequest);

        console.log(`Repair requests: ${JSON.stringify(repairRequests)}`);

        const token = await getAccessTokenSilently();

        new RepairRequestApi().saveTyreRepair(
            repairRequests,
            token,
            successCallback,
            failureCallback
        );
    };

    const getRepairRequest = (request: TyreRepairRequest) => {
        const damageTypeComment =
            request.damageType === nonUSTyreDamageTypes.Other
                ? {
                      damageTypeComment: request.damageTypeComment
                  }
                : {};

        const getRepairAddressToSend = () => {
            if (!sendRepairAddress) {
                return null;
            }

            return repairAddresses.find(
                (a) => a.id === selectedRepairAddressId
            );
        };

        const repairRequest: SaveTyreRepairRequest = {
            submitterEmailAddress: user.email,
            productPolicyId: request.productReference,
            mileage: mileage,
            damagedTyre: request.damagedTyre,
            dateOfIncident: mapJsonDateToServer(dateOfIncident),
            damageType: request.damageType,
            ...damageTypeComment,
            invoiceGuid: request?.invoice?.uploadedFileId,
            closeUpImageGuid: request.uploadedImages.find(
                (ui) => ui.type === UploadImageTypes.CloseUp
            ).imageId,
            distanceImageGuid: request.uploadedImages.find(
                (ui) => ui.type === UploadImageTypes.Distance
            ).imageId,
            additionalImage1Guid: request.uploadedImages.find(
                (ui) => ui.type === UploadImageTypes.Mileage
            ).imageId,
            additionalImage2Guid: request.uploadedImages.find(
                (ui) => ui.type === UploadImageTypes.Registration
            ).imageId,
            ...(sendRepairAddress && {
                repairAddress: getRepairAddressToSend()
            }),
            countryIso: siteLocation,
            repairOption: repairOption,
            crimeReferenceNumber: request.crimeReferenceNumber || '',
            noPaymentReason: request.noPaymentReason || ''
        };

        return repairRequest;
    };

    const mapJsonDateToServer = (dateJson: string) => {
        const cdh = new ClientDateHandler();
        cdh.parseJSON(dateJson);

        const localDate = cdh.getLocalDate();
        return localDate;
    };

    const onUpdateTyreClick = (tyre: string): void => {
        dispatch({
            type: ReducerActionNames.UpdateTyre,
            updateTyre: tyre
        });
    };

    const onUpdateAddressClick = (scrollToAddress): void => {
        dispatch({
            type: ReducerActionNames.ScrollToAddress,
            scrollToAddress: scrollToAddress
        });
    };

    const onCancelClick = () => {
        dispatch({
            type: ReducerActionNames.ShowCancelAlert,
            showCancelAlert: true
        });
    };

    const onCancelRepairRequest = () => {
        GoogleAnalytics.trackEventsGoogleAnalytics(
            'Button click',
            'Tyre button clicks',
            'Clicked cancel repair request'
        );
        dispatchAction(resetTyreRequest());
        navigate(RoutePaths.Home);
    };

    const onContinueRepairRequest = (): void => {
        if (tyreStage === TyreProcessStages.General && validateGeneralPage())
            onChangeStage(TyreProcessStages.TyreSelection);
    };

    const onCancelUndo = () => {
        dispatch({
            type: ReducerActionNames.ShowCancelAlert,
            showCancelAlert: false
        });
    };

    const onContinueToRepairAddress = (): void => {
        onChangeStage(TyreProcessStages.Summary);
    };

    const onFinishAddingTyres = (): boolean => {
        const formWasValid = validateCurrentTyreRepairRequest();

        if (formWasValid) {
            dispatch({ type: ReducerActionNames.AddTyreRequest });
            onChangeStage(TyreProcessStages.TyreSelection);
        }
        dispatch({
            type: ReducerActionNames.SetHasErrors,
            hasErrors: !formWasValid
        });

        return formWasValid;
    };

    const isImageUploaded = (images: UploadedImage[], imageType: string) => {
        return !CommonValidation.isEmpty(
            images.find((ui) => ui.type === imageType)?.uploadedPath
        );
    };

    const validateImages = (images: UploadedImage[]) => {
        const errors: Pick<
            TyreSpecificErrors,
            | 'closeUpImage'
            | 'distanceImage'
            | 'mileageImage'
            | 'registrationImage'
        > = {};

        if (isUSSite || repairOption === TyreRepairOptions.Dealer) {
            return null;
        }

        if (!isImageUploaded(images, UploadImageTypes.CloseUp)) {
            errors.closeUpImage = t('close_up_image_is_required');
        }
        if (!isImageUploaded(images, UploadImageTypes.Distance)) {
            errors.distanceImage = t('image_of_tread_is_required');
        }
        if (
            !isImageUploaded(images, UploadImageTypes.Mileage) &&
            tyreRepairRequests.length < 1 &&
            !tyreRepairRequests.some((trr) =>
                trr.uploadedImages.some(
                    (x) => x.type === UploadImageTypes.Mileage
                )
            )
        ) {
            errors.mileageImage = t('mileage_image_is_required');
        }
        if (
            !isImageUploaded(images, UploadImageTypes.Registration) &&
            tyreRepairRequests.length < 1 &&
            !tyreRepairRequests.some((trr) =>
                trr.uploadedImages.some(
                    (x) => x.type === UploadImageTypes.Registration
                )
            )
        ) {
            errors.registrationImage = t('registration_image_is_required');
        }

        return Object.keys(errors).length === 0 ? null : errors;
    };

    const validateCurrentTyreRepairRequest = () => {
        let errors: TyreSpecificErrors = {};
        let isValid = true;
        if (
            CommonValidation.isDateNull(
                currentRepairRequest.dateOfIncident,
                true
            ) ||
            CommonValidation.isEmpty(currentRepairRequest.dateOfIncident)
        ) {
            isValid = false;
            errors.dateOfIncident = t('date_of_incident_is_required');
        } else if (
            !CommonValidation.isValidDate(
                currentRepairRequest.dateOfIncident,
                true
            )
        ) {
            isValid = false;
            errors.dateOfIncident = 'Date of incident is out of a valid range';
        }

        if (
            !isUSSite &&
            repairOption !== TyreRepairOptions.Dealer &&
            preCheckRequest.hasBeenRepaired === SelectOptionsYesNo.Yes &&
            preCheckRequest.hasBeenPayed === SelectOptionsYesNo.Yes &&
            SelectInputHelper.isNullValue(currentRepairRequest.invoice)
        ) {
            isValid = false;
            errors.invoice = t('invoice_required');
        }

        if (
            isUkSite &&
            preCheckRequest.hasBeenRepaired == SelectOptionsYesNo.Yes &&
            preCheckRequest.hasBeenPayed == SelectOptionsYesNo.No &&
            CommonValidation.isEmpty(currentRepairRequest.noPaymentReason)
        ) {
            isValid = false;
            errors.noPaymentReason = t('tyre_repair_no_pay_reason_is_required');
        }

        if (SelectInputHelper.isNullValue(currentRepairRequest.damageType)) {
            isValid = false;
            errors.damageType = t('type_of_damage_is_required');
        }
        if (
            currentRepairRequest.damageType === nonUSTyreDamageTypes.Other &&
            SelectInputHelper.isNullOrEmptyalue(
                currentRepairRequest.damageTypeComment
            )
        ) {
            isValid = false;
            errors.damageTypeComment = t('tyre_damage_comment_is_required');
        }
        if (
            currentRepairRequest.damageType === nonUSTyreDamageTypes.Malicious
        ) {
            if (isUkSite) {
                if (
                    CommonValidation.isEmpty(
                        currentRepairRequest.crimeReferenceNumber
                    )
                ) {
                    isValid = false;
                    errors.crimeReferenceNumber = t(
                        'crime_reference_number_is_required'
                    );
                } else if (
                    !CommonValidation.isValidAlphaNumeric(
                        currentRepairRequest.crimeReferenceNumber
                    )
                ) {
                    isValid = false;
                    errors.crimeReferenceNumber = t(
                        'crime_reference_number_invalid'
                    );
                }
            }
        }

        const imageErrors = validateImages(currentRepairRequest.uploadedImages);
        if (imageErrors) {
            isValid = false;
            errors = {
                ...errors,
                ...imageErrors
            };
        }

        const tyreErrors: TyrePageErrors = {
            tyreSpecifics: errors
        };

        dispatch({
            type: ReducerActionNames.UpdateTyreErrors,
            tyreErrors
        });

        return isValid;
    };

    const onDeleteRepairRequest = (tyre: string) => {
        dispatch({
            type: ReducerActionNames.RemoveTyre,
            tyre: tyre,
            newStage: TyreProcessStages.TyreSelection
        });
    };

    return {
        product,
        vehicle,
        progressBarItems,
        tyreRepairRequest,
        onChangeMileage,
        onChangeRepairOption,
        onTyreValueChange,
        onTyreRepairRequestChangeTyre,
        onTyreSelected,
        onChangeDamageType,
        onRepairAddressSelectionChanged,
        onRepairAddressesSet: onRepairAddressesSet,
        onRepairAddressAdded,
        onRepairAddressUpdated,
        onDealerAddressUpdate,
        onInvoiceChange,
        onUploadImageChange,
        onTogglePermission,
        onFinishedTyreRepairRequest,
        onCancelClick,
        onCancelRepairRequest,
        onCancelUndo,
        onChangeStage,
        onUpdateTyreClick,
        onUpdateAddressClick,
        onContinueRepairRequest,
        onContinueToRepairAddress,
        onFinishAddingTyres,
        onDeleteRepairRequest,
        onChangDateOfIncident,
        onClearTyreRepairRequests
    };
}

export default useTyreRepairRequest;
